import { useMemo, useState } from "react";
import { Icon } from "./Icon";

/**
 * Lokasyon ağacı seçici — TÜM lokasyon tipleri (tesis kırılımı), tek seçim.
 * FaultReportDrawer/LineEquipTree'deki desenin genelleştirilmişi: arama (TR-normalize,
 * eşleşen + ata zinciri gösterilir → kapalı dallar da bulunur) + varsayılan-KAPALI ağaç
 * (kök seviye görünür) + chevron ile açma + seçimde ata zinciri otomatik açılır.
 */

export interface LocationTreeRow {
  id: string;
  code: string;
  name: string;
  type: string;
  parentId: string | null;
  isActive?: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  tesis: "Tesis", depo: "Depo", bolge: "Bölge", raf: "Raf",
  uretim_merkezi: "Merkez", uretim_hatti: "Hat", unite: "Ünite", makina: "Makina",
  mal_kabul: "Mal Kabul", sevkiyat: "Sevkiyat", karantina: "QC", scrap: "Scrap",
};

interface Props {
  locations: LocationTreeRow[];
  selected: string | null;
  onSelect: (id: string) => void;
  /** Verilirse yalnız bu kökün alt ağacı gösterilir; yoksa tüm kökler. */
  rootId?: string | null;
  maxHeight?: number;
}

interface Node { id: string; code: string; name: string; type: string; depth: number; parentId: string | null; hasChildren: boolean }

export function LocationTreePicker({ locations, selected, onSelect, rootId = null, maxHeight = 200 }: Props) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const nodes = useMemo(() => {
    const active = locations.filter((l) => l.isActive !== false);
    const byParent = new Map<string, LocationTreeRow[]>();
    const roots: LocationTreeRow[] = [];
    for (const r of active) {
      if (r.parentId) { const a = byParent.get(r.parentId) ?? []; a.push(r); byParent.set(r.parentId, a); }
      else roots.push(r);
    }
    const out: Node[] = [];
    const walk = (rows: LocationTreeRow[], depth: number, parentId: string | null) => {
      for (const r of [...rows].sort((a, b) => a.name.localeCompare(b.name))) {
        const kids = byParent.get(r.id) ?? [];
        out.push({ id: r.id, code: r.code, name: r.name, type: r.type, depth, parentId, hasChildren: kids.length > 0 });
        walk(kids, depth + 1, r.id);
      }
    };
    if (rootId) walk(byParent.get(rootId) ?? [], 0, null);
    else walk(roots, 0, null);
    return out;
  }, [locations, rootId]);

  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const norm = (s: string) => s.toLocaleLowerCase("tr-TR");
  const search = q.trim();

  const visible = useMemo(() => {
    if (search) {
      const needle = norm(search);
      const show = new Set<string>();
      for (const n of nodes) {
        if (norm(n.name).includes(needle) || norm(n.code).includes(needle)) {
          show.add(n.id);
          let p = n.parentId;
          while (p && byId.has(p)) { show.add(p); p = byId.get(p)!.parentId; }
        }
      }
      return nodes.filter((n) => show.has(n.id));
    }
    return nodes.filter((n) => {
      let p = n.parentId;
      while (p && byId.has(p)) { if (!expanded.has(p)) return false; p = byId.get(p)!.parentId; }
      return true;
    });
  }, [nodes, byId, search, expanded]);

  function toggle(id: string) { setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }); }
  function pick(id: string) {
    onSelect(id);
    // Ata zincirini aç — arama temizlenince seçili düğüm görünür kalsın.
    setExpanded((s) => { const n = new Set(s); let p = byId.get(id)?.parentId; while (p && byId.has(p)) { n.add(p); p = byId.get(p)!.parentId; } return n; });
  }

  return (
    <>
      <input className="form-input" type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Lokasyon ara… (ad / kod)" style={{ marginBottom: 6 }} />
      <div style={{ border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", maxHeight, overflowY: "auto", padding: 4 }}>
        {nodes.length === 0 ? (
          <div style={{ padding: "6px 10px", fontSize: 12, color: "var(--neutral-400)" }}>Lokasyon tanımlı değil.</div>
        ) : search && visible.length === 0 ? (
          <div style={{ padding: "6px 10px", fontSize: 12, color: "var(--neutral-400)" }}>"{search}" ile eşleşen lokasyon yok.</div>
        ) : visible.map((n) => (
          <div key={n.id} style={{ display: "flex", alignItems: "center", gap: 2, paddingLeft: n.depth * 16 }}>
            {n.hasChildren && !search ? (
              <button
                type="button"
                onClick={() => toggle(n.id)}
                aria-label={expanded.has(n.id) ? "Daralt" : "Genişlet"}
                aria-expanded={expanded.has(n.id)}
                style={{ width: 18, height: 18, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", background: "transparent", borderRadius: 4, cursor: "pointer", color: "var(--neutral-500)", padding: 0, transform: expanded.has(n.id) ? "rotate(90deg)" : "none", transition: "transform 100ms" }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
              </button>
            ) : (
              <span style={{ width: 18, flexShrink: 0 }} />
            )}
            <button
              type="button"
              onClick={() => pick(n.id)}
              style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, textAlign: "left", padding: "5px 8px", border: "none", borderRadius: 6, cursor: "pointer", background: selected === n.id ? "var(--primary-subtle)" : "transparent", color: "var(--neutral-800)", fontSize: 13 }}
            >
              <span style={{ width: 12, display: "inline-flex", justifyContent: "center", color: selected === n.id ? "var(--primary)" : "var(--neutral-300)" }}>
                {selected === n.id ? <Icon name="check" size={13} /> : <span style={{ width: 7, height: 7, borderRadius: 999, border: "1.5px solid currentColor", display: "inline-block" }} />}
              </span>
              <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", padding: "1px 5px", borderRadius: 4, color: "var(--neutral-600)", background: "var(--neutral-100)", whiteSpace: "nowrap" }}>{TYPE_LABELS[n.type] ?? n.type}</span>
              <span style={{ fontWeight: selected === n.id ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.name}</span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--neutral-400)", flexShrink: 0 }}>{n.code}</span>
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
